import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { QueryLoader, toast } from "@jsq/ds/core";
import { useComplianceCaseContext } from "../../entrypoints/ComplianceCaseContext";
import { ProfileRelationshipEnum } from "../../graphql/supergraphEnums";
import {
  useUpdateControllersCompletedMutation,
  useRemoveProfileFromCaseMutation,
  useGetProfilesForCaseQuery,
} from "../../graphql/supergraphHooks";
import { resolvedAccountType } from "../../utils/account_types";
import { clientContext } from "../../utils/graphql_utils";
import { ControllerIndividualSelection } from "../ControllerIndividualSelection";
import { Controller } from "../ControllerIndividualSelection/types";
import { DeleteProfileModal } from "../DeleteProfileModal";
import { ProfileToDelete } from "../DeleteProfileModal/types";
import { getAccountTypeLabel } from "../profile/utils";
import { provideLegalName } from "../ProfileTree/utils";

export const ControllersScreen = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const { caseId, basePath, isReadOnly, rootProfileId } = useComplianceCaseContext();
  const history = useHistory();

  // State for the create/edit profile modal
  const [controllerBeingDeleted, setControllerBeingDeleted] = useState<ProfileToDelete | null>(
    null,
  );

  const profileResults = useGetProfilesForCaseQuery({
    context: clientContext(),
    fetchPolicy: "no-cache",
    variables: {
      caseId: Number(caseId),
    },
  });

  const [updateControllersCompleted] = useUpdateControllersCompletedMutation({
    onError: () => toast.error("Error marking controllers as completed."),
    onCompleted: () => {
      toast.success("Controllers marked as completed successfully");
    },
    refetchQueries: ["GetProfilesForCase", "GetComplianceCase"],
    awaitRefetchQueries: true,
  });

  const [removeProfileFromCase] = useRemoveProfileFromCaseMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success("Profile deleted");
      setControllerBeingDeleted(null);
    },
    refetchQueries: ["GetProfilesForCase"],
    awaitRefetchQueries: true,
  });

  const isRoot = Number(profileId) === rootProfileId;
  const complianceCase = profileResults.data?.complianceCase;
  const allProfiles = complianceCase?.profiles.concat([complianceCase?.investingProfile]);
  const profile = allProfiles?.find((p) => p?.id === parseInt(profileId));

  // Get the entity name from the case result
  const entityName = profile?.legalName || "";

  // Transform the data into the format expected by ControllerIndividualSelection
  const controllers: Controller[] =
    profile?.controllingPersons.map((person) => {
      return {
        id: person?.childProfile?.id?.toString() || "",
        name: provideLegalName(person.childProfile),
        type: getAccountTypeLabel(resolvedAccountType(person.childProfile)),
        relationship: "Controller",
      };
    }) || [];

  const handleCompleted = async () => {
    await updateControllersCompleted({
      variables: {
        caseId: Number(caseId),
        profileId: Number(profileId),
        controllersCompleted: true,
      },
    });
    closePage();
  };

  const handleCreateController = () => {
    history.push(`${basePath}/upsert-controllers/${profileId}`);
  };

  const handleEditController = (id: string) => {
    history.push(`${basePath}/upsert-controllers/${profileId}/${id}`);
  };

  const closePage = () => {
    history.push(isRoot ? basePath : `${basePath}/overview/${profileId}`);
  };

  const handleDelete = (id: string) => {
    const controllerBeingDeletedData = id
      ? profile?.controllingPersons.find((person) => person.childProfile.id.toString() === id)
      : null;

    if (!controllerBeingDeletedData?.childProfile?.id) {
      toast.error("Could not find profile to delete");
      return;
    }

    let isOrHasPaymentBeneficiary = false;
    let descendantNames: string[] = [];

    if (complianceCase?.profiles) {
      const profileAndDescendants = complianceCase?.profiles.filter((profile) =>
        profile?.path?.includes(`/${controllerBeingDeletedData.childProfile.id}/`),
      );
      isOrHasPaymentBeneficiary = profileAndDescendants?.some((profile) =>
        profile?.parentRelationships.some(
          (relationship) =>
            relationship.relationshipType === ProfileRelationshipEnum.PaymentBeneficiary,
        ),
      );
      const re = new RegExp(`\\/${controllerBeingDeletedData.childProfile.id}\\/\\d`);
      descendantNames = profileAndDescendants
        ?.filter((profile) => re.test(profile?.path || ""))
        .map((profile) => provideLegalName(profile));
    }

    setControllerBeingDeleted({
      id: controllerBeingDeletedData.childProfile.id.toString(),
      name: provideLegalName(controllerBeingDeletedData.childProfile),
      isOrHasPaymentBeneficiary: isOrHasPaymentBeneficiary,
      descendantNames: descendantNames,
    });
  };

  const handleConfirmDelete = async (caseId: number, profileId: string) => {
    await removeProfileFromCase({
      variables: {
        caseId: Number(caseId),
        profileId: Number(profileId),
      },
    });
  };

  return (
    <QueryLoader result={profileResults}>
      <ControllerIndividualSelection
        entityName={entityName}
        controllers={controllers}
        onAddController={handleCreateController}
        onEdit={handleEditController}
        onDelete={handleDelete}
        onCompleted={handleCompleted}
        onBack={closePage}
        readOnly={isReadOnly}
      />
      <DeleteProfileModal
        profile={controllerBeingDeleted}
        caseId={Number(caseId)}
        show={!!controllerBeingDeleted}
        onClose={() => setControllerBeingDeleted(null)}
        onDelete={handleConfirmDelete}
      />
    </QueryLoader>
  );
};
